import { useQuery } from "@tanstack/react-query";
import { Menu } from "lucide-react";
import type { MouseEventHandler } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import useBreakpoint from "use-breakpoint";
import HubertLogo from "../../assets/images/hubert-logo.png";
import { fetchViewerProfileQuery } from "../../behavior/api/viewer";
import { PageRoute } from "../../constants";
import { BREAKPOINTS } from "../../utils/constants/breakpoints";
import { setCookie } from "../../utils/cookieHandlers";
import { Button, Image } from "../elements";
import "./Header.css";
import LanguageSelector from "./LanguageSelector";
import MobileNavExpanded from "./MobileNavExpanded";
import Nav from "./Nav";
import UserDropdown from "./UserDropdown";

interface HeaderProps {
    type?: "LoggedIn" | "Login" | "Registration" | "RegistrationInviteEmail" | "SignInInviteEmail" | "PublicArticle";
    interviewRedirect?: () => void;
}

interface RightSideSectionProps {
    buttonText: string;
    onClick?: MouseEventHandler;
    to?: string;
    noLanguageSelector?: boolean;
}

const RightSideSection = ({
    buttonText,
    onClick = () => "null",
    to = "",
    noLanguageSelector = false,
}: RightSideSectionProps) => (
    <div className="right-side-section-wrapper">
        {!noLanguageSelector ? <LanguageSelector /> : ""}
        <Button size="small" secondary to={to} onClick={onClick}>
            {buttonText}
        </Button>
    </div>
);

const Header = ({ type, interviewRedirect }: HeaderProps) => {
    const { t, i18n } = useTranslation();
    const [navbarExpanded, setNavbarExpanded] = useState(false);
    const { breakpoint } = useBreakpoint(BREAKPOINTS, "sm");
    const isMobile = breakpoint === "sm" || breakpoint === "md";
    const { data: viewer, isLoading: loading } = useQuery({
        queryKey: ["viewer", "profile"],
        queryFn: fetchViewerProfileQuery(),
        enabled: type === "LoggedIn",
    });

    useEffect(() => {
        if (isMobile && navbarExpanded) {
            document.body.classList.add("overflow-hidden");
        } else document.body.classList.remove("overflow-hidden");

        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, [isMobile, navbarExpanded]);

    useEffect(() => {
        const setPreferredLanguageCookie = async () => {
            const viewerLanguage = viewer?.language;
            if (viewerLanguage) {
                setCookie("language", viewerLanguage);
                i18n.changeLanguage(viewerLanguage);
            }
        };

        if (type === "LoggedIn" && !loading && viewer) {
            setPreferredLanguageCookie();
        }
    }, [viewer, i18n, loading, type]);

    const renderComponents = useCallback(() => {
        switch (type) {
            case "LoggedIn":
                return !isMobile ? (
                    <>
                        <Nav isMobile={isMobile} />
                        <UserDropdown isMobile={isMobile} viewer={viewer} loading={loading} />
                    </>
                ) : (
                    <Menu className="menu-icon" onClick={() => setNavbarExpanded((prevState) => !prevState)} />
                );
            case "Registration":
                return <RightSideSection buttonText={t("Header.ButtonText_SignInToYourAccount")} to="/" />;
            case "Login":
                return (
                    <RightSideSection
                        buttonText={t("Header.ButtonText_CreateNewAccount")}
                        to={PageRoute.REGISTRATION}
                    />
                );
            case "RegistrationInviteEmail":
                return (
                    <RightSideSection
                        buttonText={t("Header.ButtonText_StartInterviewWithoutSigningUp")}
                        onClick={interviewRedirect}
                    />
                );
            case "SignInInviteEmail":
                return (
                    <RightSideSection
                        buttonText={t("Header.ButtonText_StartInterviewWithoutSigningIn")}
                        onClick={interviewRedirect}
                    />
                );
            case "PublicArticle":
                return (
                    <RightSideSection
                        buttonText={t("Header.ButtonText_SignInToYourAccount")}
                        to="/"
                        noLanguageSelector
                    />
                );
            default:
                return <LanguageSelector />;
        }
    }, [type, isMobile, t, interviewRedirect, viewer, loading, setNavbarExpanded]);

    return (
        <div className="header-wrapper">
            {type === "LoggedIn" ? (
                <Link to={PageRoute.DASHBOARD}>
                    <Image className="hubert-logo" src={HubertLogo} alt="hubert-logo" />
                </Link>
            ) : (
                <a href="/">
                    <Image className="hubert-logo" src={HubertLogo} alt="hubert-logo" />
                </a>
            )}
            {renderComponents()}
            {type === "LoggedIn" && isMobile && navbarExpanded && (
                <MobileNavExpanded isMobile={isMobile} setNavbarExpanded={setNavbarExpanded} />
            )}
        </div>
    );
};

export default Header;
